import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Grid,
  Tabs,
  Tab,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Download,
  Upload,
  Event,
  LocationOn,
  CalendarToday,
  CheckCircle,
  Pending,
  Cancel,
  Info,
  CloudUpload,
  Description,
  People,
  AttachMoney,
  Payment,
  Receipt,
  CloudDownload,
  PausePresentationOutlined,
  Email,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { stateAssociationAPI } from '../../api/stateAssociation.api';
import { paymentAPI } from '../../api/payment.api';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid #e0e0e0',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(41, 30, 108, 0.15)',
    transform: 'translateY(-4px)',
  },
}));

const StyledButton = styled(Button)({
  padding: '10px 24px',
  borderRadius: '8px',
  fontSize: '0.95rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
});

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const AthleteEventsManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successPaymentData, setSuccessPaymentData] = useState(null);
  // Payment breakdown dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentBreakdown, setPaymentBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [tabValue]);

  useEffect(() => {
    loadRazorpayScript();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await stateAssociationAPI.getAssignedEvents({ 
        page: 1,
        limit: 50,
        sortBy: 'startDate',
        sortOrder: 'asc'
      });
      
      console.log('Fetched events:', response.data); // Debug
      
      let filteredEvents = response.data || [];
      
      if (tabValue === 0) {
        // Pending applications
        filteredEvents = filteredEvents.filter(
          event => !event.application || 
          ['draft', 'submitted', 'under_review', 'payment_pending'].includes(event.application?.applicationStatus)
        );
      } else if (tabValue === 1) {
        // Approved/Completed payments
        filteredEvents = filteredEvents.filter(
          event => ['approved', 'payment_completed'].includes(event.application?.applicationStatus)
        );
      } else if (tabValue === 2) {
        // Past events
        filteredEvents = filteredEvents.filter(
          event => new Date(event.endDate) < new Date()
        );
      }
      
      setEvents(filteredEvents);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch events');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async (event) => {
    try {
      const response = await stateAssociationAPI.downloadEventTemplate(event._id);
      window.open(response.data.downloadUrl || response.data.url, '_blank');
      toast.success('Template download started');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to download template');
    }
  };

  const handleDownloadApplication = async (event) => {
    try {
      if (event.application?.uploadedFile?.path) {
        window.open(event.application.uploadedFile.path, '_blank');
        toast.success('Download started');
      } else {
        toast.error('Application file not found');
      }
    } catch (err) {
      toast.error('Failed to download application');
    }
  };

  const handleOpenUploadDialog = (event) => {
    setSelectedEvent(event);
    setUploadDialogOpen(true);
    setUploadFile(null);
    setValidationErrors([]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid Excel file (.xlsx or .xls)');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setUploadFile(file);
    }
  };

  const handleUploadApplication = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    setValidationErrors([]);

    try {
      const response = await stateAssociationAPI.uploadEventApplication(
        selectedEvent._id,
        uploadFile
      );

      toast.success(`Application submitted successfully! Total Participants: ${response.data.totalParticipants}`);
      setUploadDialogOpen(false);
      fetchEvents();
    } catch (err) {
      const errorData = err.response?.data;
      
      if (errorData?.errors && errorData.errors.length > 0) {
        setValidationErrors(errorData.errors);
        toast.error(`Found ${errorData.totalErrors} validation error(s) in the file`);
      } else {
        toast.error(errorData?.message || 'Failed to upload application');
      }
    } finally {
      setUploading(false);
    }
  };


  const handleInitiatePayment = async (event) => {
  setSelectedEvent(event);
  setPaymentDialogOpen(true);
  setPaymentBreakdown(null);
  setLoadingBreakdown(true);

  // FETCH BREAKDOWN IMMEDIATELY ON DIALOG OPEN
  try {
    const orderResponse = await paymentAPI.createEventApplicationOrder(
      event.application._id
    );

    const { priceBreakdown } = orderResponse.data;
    
    // Set the breakdown immediately
    setPaymentBreakdown(priceBreakdown);
    
    // Store order data for later use
    setSelectedEvent({
      ...event,
      orderData: orderResponse.data
    });
    
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to load payment details');
    console.error('Error loading breakdown:', error);
  } finally {
    setLoadingBreakdown(false);
  }
};

const handleConfirmAndPay = async () => {
  setPaymentProcessing(true);

  try {
    // Use the order data that was already fetched
    const orderData = selectedEvent.orderData;

    if (!orderData) {
      toast.error('Order data not found. Please try again.');
      setPaymentProcessing(false);
      return;
    }

    // Initialize Razorpay directly
    const options = {
      key: orderData.key,
      amount: orderData.amount,
      currency: 'INR',
      name: 'Indian Weightlifting Federation',
      description: `Payment for ${orderData.eventTitle || selectedEvent.title}`,
      order_id: orderData.orderId,
      prefill: {
        name: orderData.stateAssociationName,
        email: orderData.stateAssociationEmail,
        contact: orderData.stateAssociationPhone,
      },
      theme: {
        color: '#291e6c',
      },
      handler: async function (response) {
        try {
          // Verify payment
          const verifyResponse = await paymentAPI.verifyEventApplicationPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            applicationId: selectedEvent.application._id,
          });

          // Close payment dialog
          setPaymentDialogOpen(false);
          setPaymentProcessing(false);
          
          // Show success dialog with payment data
          setSuccessPaymentData({
            ...verifyResponse.data,
            eventTitle: selectedEvent.title,
            razorpayPaymentId: response.razorpay_payment_id,
          });
          setSuccessDialogOpen(true);
          
          // Refresh events
          fetchEvents();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Payment verification failed');
          setPaymentProcessing(false);
        }
      },
      modal: {
        ondismiss: function () {
          setPaymentProcessing(false);
          toast.info('Payment cancelled');
        },
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  } catch (err) {
    toast.error(err.response?.data?.message || 'Failed to initiate payment');
    setPaymentProcessing(false);
  }
};


  const handleDownloadInvoice = async (application) => {
    try {
        window.open(application.invoicePath, '_blank');
        toast.success('Invoice download started');
    } catch (error) {
      toast.error('Failed to download invoice');
      console.error(error);
    }
  };

    const handleDownloadCertificate = async (application) => {
    try {
        window.open(application.participationCertificatePath, '_blank');
        toast.success('Certificate download started');
    } catch (error) {
      toast.error('Failed to download certificate');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
      case 'submitted':
        return 'warning';
      case 'under_review':
        return 'info';
      case 'payment_pending':
        return 'secondary';
      case 'approved':
      case 'payment_completed':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
      case 'payment_completed':
        return <CheckCircle fontSize="small" />;
      case 'submitted':
      case 'under_review':
      case 'payment_pending':
        return <Pending fontSize="small" />;
      case 'rejected':
        return <Cancel fontSize="small" />;
      default:
        return <Info fontSize="small" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

const formatAmount = (amount) => {
  return (amount / 100).toFixed(2);
};

  const EventCard = ({ event }) => {
    const hasApplication = event.application;
    const applicationStatus = hasApplication?.applicationStatus;
    const paymentStatus = hasApplication?.paymentStatus;
    const canApply = !hasApplication || applicationStatus === 'draft';
    const needsPayment = applicationStatus === 'payment_pending' && paymentStatus !== 'completed';
    const paymentCompleted = paymentStatus === 'completed';
    const hasUploadedFile = hasApplication?.uploadedFile?.path;
    const isDeadlinePassed = new Date() > new Date(event.lastDateOfApplication);
    const hasFinalAmount = hasApplication?.finalAmount && hasApplication.finalAmount > 0;

    console.log('Event Card Debug:', {
      eventId: event._id,
      hasApplication,
      applicationStatus,
      paymentStatus,
      canApply,
      needsPayment,
      hasUploadedFile,
      hasFinalAmount,
      finalAmount: hasApplication?.finalAmount
    });

    return (
      <StyledCard>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#291e6c' }}>
            {event.title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
            {event.description?.substring(0, 150)}
            {event.description?.length > 150 && '...'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1} sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <LocationOn fontSize="small" color="action" />
              <Typography variant="body2">{event.venue}</Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2">
                {formatDate(event.startDate)} - {formatDate(event.endDate)}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Event fontSize="small" color="action" />
              <Typography variant="body2" color={isDeadlinePassed ? 'error' : 'success.main'}>
                Apply by: {formatDate(event.lastDateOfApplication)}
              </Typography>
            </Box>
          </Stack>

          {hasApplication && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(41, 30, 108, 0.05)', borderRadius: 1 }}>
              <Chip
                icon={getStatusIcon(applicationStatus)}
                label={applicationStatus?.replace('_', ' ').toUpperCase()}
                color={getStatusColor(applicationStatus)}
                size="small"
                sx={{ mb: 1 }}
              />
              
              {hasApplication.totalParticipants > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<People fontSize="small" />}
                    label={`${hasApplication.totalParticipants} Participants`}
                    size="small"
                    variant="outlined"
                  />
                  {hasApplication.finalAmount > 0 && (
                    <Chip
                      icon={<AttachMoney fontSize="small" />}
                      label={`₹${hasApplication.finalAmount.toLocaleString()}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              )}

              {/* {needsPayment && hasFinalAmount && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="600">
                    Payment Required: ₹{hasApplication.finalAmount.toLocaleString()}
                  </Typography>
                  {hasApplication.adminModifiedAmount && (
                    <Typography variant="caption" display="block">
                      {hasApplication.amountModificationReason || 'Amount modified by admin'}
                    </Typography>
                  )}
                </Alert>
              )} */}

              {needsPayment && !hasFinalAmount && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    Waiting for admin to set final payment amount
                  </Typography>
                </Alert>
              )}

              {paymentCompleted && hasApplication.invoiceNumber && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                  <Receipt fontSize="small" color="primary" />
                  <Typography variant="body2" color="primary">
                    Invoice: {hasApplication.invoiceNumber}
                  </Typography>
                </Stack>
              )}
            </Box>
          )}

          <Stack spacing={1.5}>
            {/* Download Template - Only show when no application OR application is draft */}
            {canApply && !isDeadlinePassed && (
              <StyledButton
                variant="outlined"
                startIcon={<Download />}
                onClick={() => handleDownloadTemplate(event)}
                size="small"
                sx={{ borderColor: '#291e6c', color: '#291e6c' }}
              >
                Download Template
              </StyledButton>
            )}
            
            {/* Upload Application - Only show when can apply */}
            {canApply && !isDeadlinePassed && (
              <StyledButton
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => handleOpenUploadDialog(event)}
                size="small"
                sx={{ bgcolor: '#291e6c', '&:hover': { bgcolor: '#1a1247' } }}
              >
                Upload Application
              </StyledButton>
            )}

            {/* Download Uploaded File - Show if file exists */}
            {hasUploadedFile && (
              <StyledButton
                variant="outlined"
                startIcon={<CloudDownload />}
                onClick={() => handleDownloadApplication(event)}
                size="small"
                color="info"
              >
                Download Application File
              </StyledButton>
            )}
            
            {/* Payment Button - Show when payment is pending and amount is set */}
            {needsPayment && hasFinalAmount && (
              <StyledButton
                variant="contained"
                startIcon={<Payment />}
                onClick={() => handleInitiatePayment(event)}
                size="small"
                color="success"
                disabled={paymentProcessing}
              >
                {paymentProcessing ? 'Processing...' : 'Make Payment'}
              </StyledButton>
            )}

            {/* Download Invoice - Show after payment completion */}
            {paymentCompleted && hasApplication?.invoicePath && (
              <StyledButton
                variant="contained"
                color="primary"
                startIcon={<Receipt />}
                onClick={() => handleDownloadInvoice(hasApplication)}
                size="small"
              >
                Download Invoice
              </StyledButton>
            )}

            {paymentCompleted && hasApplication?.participationCertificatePath && (
              <StyledButton
                variant="contained"
                color="warning"
                startIcon={<PausePresentationOutlined />}
                onClick={() => handleDownloadCertificate(hasApplication)}
                size="small"
              >
                Download Certificate
              </StyledButton>
            )}
            
            {isDeadlinePassed && !hasApplication && (
              <Chip label="Registration Closed" color="error" size="small" />
            )}
          </Stack>
        </CardContent>
      </StyledCard>
    );
  };

  return (
    <Container maxWidth="lg">

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
            },
            '& .Mui-selected': {
              color: '#291e6c',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#291e6c',
            },
          }}
        >
          <Tab label="Pending Applications" />
          <Tab label="Approved" />
          <Tab label="Completed Events" />
        </Tabs>
      </Box>

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      <TabPanel value={tabValue} index={0}>
        {events.length === 0 ? (
          <Alert severity="info">No pending events found</Alert>
        ) : (
          <Grid container spacing={3}>
            {events.map((event) => (
              <Grid item xs={12} md={6} lg={4} key={event._id}>
                <EventCard event={event} />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {events.length === 0 ? (
          <Alert severity="info">No approved applications found</Alert>
        ) : (
          <Grid container spacing={3}>
            {events.map((event) => (
              <Grid item xs={12} md={6} lg={4} key={event._id}>
                <EventCard event={event} />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {events.length === 0 ? (
          <Alert severity="info">No completed events found</Alert>
        ) : (
          <Grid container spacing={3}>
            {events.map((event) => (
              <Grid item xs={12} md={6} lg={4} key={event._id}>
                <EventCard event={event} />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CloudUpload color="primary" />
            <Typography variant="h6">Upload Application</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedEvent && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please download the template, fill it with participant details, and upload it back.
              </Alert>

              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Event:</strong> {selectedEvent.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Venue:</strong> {selectedEvent.venue}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Last Date:</strong> {formatDate(selectedEvent.lastDateOfApplication)}
                </Typography>
              </Box>

              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="upload-file"
                type="file"
                onChange={handleFileChange}
                disabled={uploading}
              />
              <label htmlFor="upload-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Description />}
                  fullWidth
                  disabled={uploading}
                  sx={{
                    height: 56,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                  }}
                >
                  {uploadFile ? uploadFile.name : 'Choose Excel File'}
                </Button>
              </label>

              {uploadFile && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  File selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(2)} KB)
                </Alert>
              )}

              {validationErrors.length > 0 && (
                <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                  <Alert severity="error" sx={{ mb: 1 }}>
                    Please fix the following errors and re-upload:
                  </Alert>
                  {validationErrors.slice(0, 10).map((error, index) => (
                    <Alert severity="warning" key={index} sx={{ mb: 1 }}>
                      <strong>Row {error.row}:</strong> {error.field} - {error.message}
                    </Alert>
                  ))}
                  {validationErrors.length > 10 && (
                    <Typography variant="caption" color="text.secondary">
                      ... and {validationErrors.length - 10} more errors
                    </Typography>
                  )}
                </Box>
              )}

              {uploading && <LinearProgress sx={{ mt: 2 }} />}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setUploadDialogOpen(false)} 
            disabled={uploading}
          >
            Cancel
          </Button>
          <StyledButton
            onClick={handleUploadApplication}
            variant="contained"
            disabled={!uploadFile || uploading}
            sx={{ bgcolor: '#291e6c', '&:hover': { bgcolor: '#1a1247' } }}
          >
            {uploading ? 'Uploading...' : 'Submit Application'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Payment Breakdown Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => !paymentProcessing && setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoney color="success" />
            <Typography variant="h6">Payment Breakdown</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedEvent?.application && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Event:</strong> {selectedEvent.title}
                </Typography>
                <Typography variant="body2">
                  <strong>Participants:</strong> {selectedEvent.application.totalParticipants}
                </Typography>
              </Alert>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          Base Amount
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600">
                          ₹{selectedEvent.application.finalAmount?.toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {paymentBreakdown && (
                      <>
                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">GST (18%)</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              ₹{paymentBreakdown.gstAmountInRupees}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">
                              Payment Gateway Fee (2%)
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              ₹{paymentBreakdown.convenienceAmountInRupees}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={2}>
                            <Divider sx={{ my: 1 }} />
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>
                            <Typography variant="h6" fontWeight="bold">
                              Total Amount
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" color="success.main" fontWeight="bold">
                              ₹{paymentBreakdown.totalAmountInRupees}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {loadingBreakdown && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <CircularProgress size={30} />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Preparing payment gateway...
                  </Typography>
                </Box>
              )}

              {!paymentBreakdown && !loadingBreakdown && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Click <strong>"Confirm and Pay"</strong> to proceed. You will see the complete breakdown including GST and payment gateway charges.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setPaymentDialogOpen(false);
              setPaymentBreakdown(null);
              setPaymentProcessing(false);
            }}
            disabled={paymentProcessing || loadingBreakdown}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAndPay}
            variant="contained"
            color="success"
            disabled={paymentProcessing || loadingBreakdown}
            startIcon={
              paymentProcessing || loadingBreakdown ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Payment />
              )
            }
            sx={{ minWidth: 180 }}
          >
            {paymentProcessing || loadingBreakdown ? 'Processing...' : 'Confirm and Pay'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Success Dialog */}
<Dialog 
  open={successDialogOpen} 
  onClose={() => {
    setSuccessDialogOpen(false);
    setSuccessPaymentData(null);
  }}
  maxWidth="md"
  fullWidth
>
  <DialogContent sx={{ textAlign: 'center', py: 4 }}>
    {/* Success Icon */}
    <CheckCircle 
      sx={{ 
        fontSize: 80, 
        color: '#4caf50',
        mb: 2,
        filter: 'drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3))'
      }} 
    />
    
    {/* Success Title */}
    <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#291e6c' }}>
      Payment Successful!
    </Typography>
    
    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
      Your event application payment has been completed successfully
    </Typography>

    {successPaymentData && (
      <>
        {/* Payment Details Card */}
        <Card sx={{ mb: 3, backgroundColor: 'rgba(41, 30, 108, 0.05)', border: '2px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#291e6c', mb: 2 }}>
              Payment Details
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <Stack spacing={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Event</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {successPaymentData.eventTitle}
                </Typography>
              </Box>
              
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Invoice Number</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {successPaymentData.invoiceNumber}
                </Typography>
              </Box>
              
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Payment Date</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDate(successPaymentData.paymentDate)}
                </Typography>
              </Box>
              
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Amount Paid</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">
                  ₹{formatAmount(successPaymentData.amount)}
                </Typography>
              </Box>
              
              {successPaymentData.razorpayPaymentId && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Payment ID</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                    {successPaymentData.razorpayPaymentId}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} justifyContent="center">
          {successPaymentData.invoicePath && (
            <StyledButton
              variant="contained"
              startIcon={<Receipt />}
              onClick={() => {
                window.open(successPaymentData.invoicePath, '_blank');
                toast.success('Invoice opened in new tab');
              }}
              sx={{ bgcolor: '#291e6c', '&:hover': { bgcolor: '#1a1247' } }}
            >
              Download Invoice
            </StyledButton>
          )}
          
          {successPaymentData.participationCertificatePath && (
            <StyledButton
              variant="outlined"
              startIcon={<PausePresentationOutlined />}
              onClick={() => {
                window.open(successPaymentData.participationCertificatePath, '_blank');
                toast.success('Certificate opened in new tab');
              }}
              sx={{ 
                borderColor: '#291e6c', 
                color: '#291e6c',
                '&:hover': { borderColor: '#1a1247', bgcolor: 'rgba(41, 30, 108, 0.05)' }
              }}
            >
              Download Certificate
            </StyledButton>
          )}
        </Stack>

        {/* Info Alerts */}
        <Alert severity="success" sx={{ mt: 3, textAlign: 'left' }} icon={<Email />}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Confirmation Sent
          </Typography>
          <Typography variant="body2">
            A confirmation email with your invoice has been sent to your registered email address.
          </Typography>
        </Alert>
      </>
    )}
  </DialogContent>
  
  <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
    <StyledButton
      variant="outlined"
      onClick={() => {
        setSuccessDialogOpen(false);
        setSuccessPaymentData(null);
      }}
      sx={{ 
        minWidth: 150,
        borderColor: '#291e6c',
        color: '#291e6c'
      }}
    >
      Close
    </StyledButton>
  </DialogActions>
</Dialog>

    </Container>
  );
};

export default AthleteEventsManagement;